import Product from '@models/product';
import CheckboxIcon from '@repo/ui/src/components/Icons/CheckboxIcon';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import HelpIconTooltip from '@components/shared/HelpIconTooltip';
import { classNames } from '@repo/ui/src/utils/classNames';
import ProductPrice from '../Price';
import SelectedCheckboxIcon from '@repo/ui/src/components/Icons/SelectedCheckboxIcon';

export interface EssentialLineItemProps {
  product: Product;
  handleSelect: () => void;
  selected?: boolean;
}

const EssentialLineItem = ({ product, handleSelect, selected = false }: EssentialLineItemProps) => {
  const { t } = useTranslation();
  const imgUrl = product.getMainImageSize(112, 1, 1);

  const outOfStock = !product.isOrderable;

  return (
    <div
      className={classNames(
        'tw-grid tw-gap-4 tw-grid-cols-[auto_3fr_auto] tw-min-h-[88px] tw-py-4 tw-p-6 tw-bg-white tw-border-4 tw-text-neutral-700',
        outOfStock && 'tw-bg-warm-300',
        selected ? 'tw-border-brand-300' : 'tw-border-white',
      )}
    >
      <div className="tw-flex tw-gap-x-6">
        <label className="tw-relative tw-flex tw-items-center tw-cursor-pointer">
          <input
            type="checkbox"
            className="tw-sr-only"
            checked={selected}
            disabled={outOfStock}
            tabIndex={0}
            onChange={handleSelect}
            aria-label="primary checkbox"
            aria-checked={selected}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSelect();
              }
            }}
          />
          <span className="tw-block">{selected ? <SelectedCheckboxIcon /> : <CheckboxIcon />}</span>
        </label>
        <div>
          <LazyLoadImage
            imgComponent={
              <img
                className="tw-w-14 tw-h-14"
                src={imgUrl}
                alt={product.title || 'essential accessory'}
              />
            }
          />
        </div>
      </div>
      <div className="tw-flex tw-flex-col tw-justify-center">
        <p className="tra-text-web-body-l tw-text-black">
          {product.title}
          <HelpIconTooltip
            label={`${product.title} product information dialog`}
            closeLabel={t('genericContent.closeIconButtonLabel', undefined, {
              default: 'close information dialog',
            })}
            text={product.description}
            altColor={'#727170'}
          />
        </p>
        {outOfStock && (
          <div className="tw-bg-neutral-300 tw-text-center tw-max-w-[110px] tw-text-white tw-mt-1">
            <p className="tw-text-center tra-text-web-utl-label-s tw-text-warm-100">
              {t('productDetailPageContent.outOfStockText', undefined, {
                default: 'out of stock',
              })}
            </p>
          </div>
        )}
      </div>
      <div className="tw-flex tw-justify-right tw-items-center">
        <ProductPrice
          product={product}
          className="[&_del]:lg:tw-pt-0 [&_del]:tw-text-center tw-flex-col"
          align="right"
          variant="h7"
          outOfStock={outOfStock}
        />
      </div>
    </div>
  );
};

export default EssentialLineItem;
